import { findEventEntry } from "@repo/vihangam-yog-shared";
import { ChevronDown } from "lucide-react";
import { useId, useMemo, useState } from "react";

import { eventHasFinished, isEventToday } from "../../lib/eventTiming";
import type { EventGroup } from "../event-registrations/shared";

import { EmailRow } from "./EmailRow";
import {
  entryPassAudience,
  followUpAudience,
  kundAudience,
  notArrivedAudience,
  pastGuestAudience,
  reminderAudience,
} from "./audience";
import { DirectionsPanel } from "./panels/DirectionsPanel";
import { EntryPassPanel } from "./panels/EntryPassPanel";
import { FollowUpPanel } from "./panels/FollowUpPanel";
import { PastGuestsPanel } from "./panels/PastGuestsPanel";
import { RegentsParkTodayPanel } from "./panels/RegentsParkTodayPanel";
import { ReminderPanel } from "./panels/ReminderPanel";
import { SeeYouTomorrowPanel } from "./panels/SeeYouTomorrowPanel";
import { TonightPanel } from "./panels/TonightPanel";
import { YourKundPanel } from "./panels/YourKundPanel";
import { people } from "./sendCopy";
import {
  alreadySentNote,
  formatSentAt,
  lastSentLine,
  latestSend,
  type EmailSend,
} from "./sendHistory";
import { BODY_TEXT, BUTTON_DISCLOSURE } from "./ui";

/** The venue whose entrance the directions email shows. The backend's
 *  template is written for this one entrance, so no other event gets it. */
const AHMA_VENUE_NAME = "Australian Hindu Multicultural Association (AHMA)";

function isAtAhma(eventSlug: string): boolean {
  return findEventEntry(eventSlug)?.venue?.name === AHMA_VENUE_NAME;
}

/** The one event with its own on-the-day email: the run of the evening.
 *  The backend's template is written for it alone. */
const TONIGHT_EVENT_SLUG = "but-why-sep-17";

/** Regents Park: on the day, a last reminder to Sydney people not yet
 *  invited to it. */
const REGENTS_PARK_EVENT_SLUG = "regents-park-sep-21";

/** The Hawan: bookings sit at allocated Kunds and get a "Your Kund" email. */
const HAWAN_EVENT_SLUG = "hawan-yajna-sep-19";

type Phase = "before" | "day" | "after";

type EmailId =
  | "entry-pass"
  | "reminder"
  | "past-guests"
  | "directions"
  | "tonight"
  | "regents-today"
  | "see-you-tomorrow"
  | "your-kund"
  | "thank-you"
  | "sorry";

const PHASE = {
  before: {
    title: "Before the event",
    name: "before the event",
    hint: "Usually sent before the event.",
  },
  day: {
    title: "On the day",
    name: "on the day",
    hint: "Usually sent on the day, to people still on their way.",
  },
  after: {
    title: "After the event",
    name: "after the event",
    hint: "Usually sent once the event has finished.",
  },
} satisfies Record<Phase, { title: string; name: string; hint: string }>;

/** "Goes to the 14 people who booked", or a plain line when no one. */
function goesTo(count: number, who: string): string {
  return count === 0
    ? "No one to send this to yet"
    : `Goes to the ${people(count)} ${who}`;
}

/**
 * "2. Choose the email": every email for one event, grouped the way an
 * event happens (before, on the day, after). The group for where the
 * event is now is open and marked Now; the others sit behind a button.
 * One email is open at a time.
 */
export function EventEmails({
  group,
  events,
  history,
  defaultTestEmail,
  senderName,
}: {
  group: EventGroup;
  /** Every event's bookings, for counting past guests. */
  events: readonly EventGroup[];
  /** The send history; undefined while it loads or when it failed. */
  history: readonly EmailSend[] | undefined;
  defaultTestEmail: string;
  senderName: string | null;
}) {
  const headingId = useId();
  const now = new Date();
  const slug = group.eventSlug;
  const finished = eventHasFinished(group, now);
  const today = isEventToday(group, now);
  const directions = isAtAhma(slug);
  const tonight = slug === TONIGHT_EVENT_SLUG;
  const current: Phase = finished
    ? "after"
    : today && (directions || tonight)
      ? "day"
      : "before";

  const [openEmail, setOpenEmail] = useState<EmailId | null>(
    current === "day" ? (directions ? "directions" : "tonight") : null,
  );
  const [shownPhases, setShownPhases] = useState<readonly Phase[]>([]);
  const pastGuests = useMemo(
    () => pastGuestAudience(events, group),
    [events, group],
  );

  const phases: { phase: Phase; emails: EmailId[] }[] = [
    {
      phase: "before",
      emails: [
        ...(slug === REGENTS_PARK_EVENT_SLUG
          ? (["regents-today"] as const)
          : []),
        ...(slug === HAWAN_EVENT_SLUG
          ? (["see-you-tomorrow", "your-kund"] as const)
          : []),
        "entry-pass",
        "reminder",
        "past-guests",
      ],
    },
  ];
  if (directions || tonight) {
    phases.push({
      phase: "day",
      emails: [
        ...(directions ? (["directions"] as const) : []),
        ...(tonight ? (["tonight"] as const) : []),
      ],
    });
  }
  phases.push({ phase: "after", emails: ["thank-you", "sorry"] });

  const toggleEmail = (id: EmailId) =>
    setOpenEmail((open) => (open === id ? null : id));
  const togglePhase = (phase: Phase) =>
    setShownPhases((shown) =>
      shown.includes(phase)
        ? shown.filter((p) => p !== phase)
        : [...shown, phase],
    );

  const status = (type: string) => lastSentLine(history, slug, [type]);
  const note = (type: string) => alreadySentNote(history, slug, [type]);

  const renderEmail = (id: EmailId) => {
    const open = openEmail === id;
    const onToggle = () => toggleEmail(id);
    switch (id) {
      case "entry-pass": {
        const count = entryPassAudience(group);
        return (
          <EmailRow
            key={id}
            level={4}
            title="Entry pass (QR code)"
            audienceLine={goesTo(count, "who booked, not walk-ins")}
            statusLine={status("qr_pass")}
            open={open}
            onToggle={onToggle}
          >
            <EntryPassPanel
              eventSlug={slug}
              audienceCount={count}
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
              alreadySentNote={note("qr_pass")}
            />
          </EmailRow>
        );
      }
      case "reminder": {
        const count = reminderAudience(group);
        const last = history
          ? latestSend(history, slug, ["reminder"])
          : undefined;
        return (
          <EmailRow
            key={id}
            level={4}
            title="Reminder"
            audienceLine={goesTo(count, "who booked")}
            statusLine={status("reminder")}
            open={open}
            onToggle={onToggle}
          >
            <ReminderPanel
              eventSlug={slug}
              audienceCount={count}
              bookingCount={group.registrationCount}
              remindedCount={group.remindedCount}
              lastSentOn={last ? formatSentAt(last.createdAt) : null}
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
            />
          </EmailRow>
        );
      }
      case "past-guests":
        return (
          <EmailRow
            key={id}
            level={4}
            title="Invite past guests"
            audienceLine={
              pastGuests === 0
                ? "No past guests to invite yet"
                : `Goes to ${people(pastGuests)} who came to an earlier event but ${pastGuests === 1 ? "hasn't" : "haven't"} booked for this one`
            }
            statusLine={status("broadcast")}
            open={open}
            onToggle={onToggle}
          >
            <PastGuestsPanel
              eventSlug={slug}
              eventTitle={group.eventTitle}
              audienceCount={pastGuests}
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
              alreadySentNote={note("broadcast")}
            />
          </EmailRow>
        );
      case "directions": {
        const count = notArrivedAudience(group);
        return (
          <EmailRow
            key={id}
            level={4}
            title="Directions to the entrance"
            audienceLine={goesTo(
              count,
              count === 1
                ? "who hasn't arrived yet"
                : "who haven't arrived yet",
            )}
            statusLine={status("directions")}
            open={open}
            onToggle={onToggle}
          >
            <DirectionsPanel
              eventSlug={slug}
              audienceCount={count}
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
              alreadySentNote={note("directions")}
            />
          </EmailRow>
        );
      }
      case "regents-today":
        return (
          <EmailRow
            key={id}
            level={4}
            title="Tonight, the last evening in Sydney (people not yet invited)"
            audienceLine="Goes to Sydney people who haven't registered or been invited to this"
            statusLine={status("regents_park_today")}
            open={open}
            onToggle={onToggle}
          >
            <RegentsParkTodayPanel
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
              alreadySentNote={note("regents_park_today")}
            />
          </EmailRow>
        );
      case "see-you-tomorrow": {
        const count = entryPassAudience(group);
        return (
          <EmailRow
            key={id}
            level={4}
            title="See you tomorrow (where, when and parking)"
            audienceLine={goesTo(count, "who booked, not walk-ins")}
            statusLine={status("hawan_see_you_tomorrow")}
            open={open}
            onToggle={onToggle}
          >
            <SeeYouTomorrowPanel
              eventSlug={slug}
              audienceCount={count}
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
              alreadySentNote={note("hawan_see_you_tomorrow")}
            />
          </EmailRow>
        );
      }
      case "your-kund": {
        const { withKund, withoutKund } = kundAudience(group);
        return (
          <EmailRow
            key={id}
            level={4}
            title="Your Kund (with the map and entry pass)"
            audienceLine={goesTo(withKund, "with a Kund allocated")}
            statusLine={status("hawan_allocation")}
            open={open}
            onToggle={onToggle}
          >
            <YourKundPanel
              eventSlug={slug}
              audienceCount={withKund}
              withoutKund={withoutKund}
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
              alreadySentNote={note("hawan_allocation")}
            />
          </EmailRow>
        );
      }
      case "tonight": {
        const count = entryPassAudience(group);
        return (
          <EmailRow
            key={id}
            level={4}
            title="Tonight (how the evening runs)"
            audienceLine={goesTo(count, "who booked, not walk-ins")}
            statusLine={status("butwhy_tonight")}
            open={open}
            onToggle={onToggle}
          >
            <TonightPanel
              audienceCount={count}
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
              alreadySentNote={note("butwhy_tonight")}
            />
          </EmailRow>
        );
      }
      case "thank-you":
      case "sorry": {
        const audience = id === "thank-you" ? "attended" : "absent";
        const type =
          audience === "attended" ? "follow_up_attended" : "follow_up_absent";
        const count = followUpAudience(group, audience);
        return (
          <EmailRow
            key={id}
            level={4}
            title={
              audience === "attended"
                ? "Thank you for coming"
                : "Sorry we missed you"
            }
            audienceLine={goesTo(
              count,
              audience === "attended"
                ? "who came"
                : "who booked but didn't come",
            )}
            statusLine={status(type)}
            open={open}
            onToggle={onToggle}
          >
            <FollowUpPanel
              eventSlug={slug}
              audience={audience}
              audienceCount={count}
              defaultTestEmail={defaultTestEmail}
              senderName={senderName}
              alreadySentNote={note(type)}
            />
          </EmailRow>
        );
      }
    }
  };

  const intro = finished
    ? "This event has finished. Thank the people who came, or send a kind note to those who booked but didn't come."
    : today
      ? directions
        ? "This event is on today. Send directions to people still on their way."
        : tonight
          ? "This event is on tonight. Send everyone who booked how the evening runs."
          : "This event is on today. Guests can still get their entry pass or a reminder."
      : group.eventStartsAt
        ? "This event hasn't happened yet. Send guests their entry pass and a reminder, or invite past guests."
        : "This is a weekly event. These emails go to the people who have booked for it.";

  return (
    <section aria-labelledby={headingId} className="mt-12">
      <h2
        id={headingId}
        className="text-title font-bold text-[color:var(--color-ink)]"
      >
        2. Choose the email
      </h2>
      <p className={`mt-2 max-w-2xl ${BODY_TEXT}`}>{intro}</p>

      {phases.map(({ phase, emails }) => {
        const isCurrent = phase === current;
        const shown = isCurrent || shownPhases.includes(phase);
        return (
          <div key={phase} className="mt-6">
            {isCurrent ? null : (
              <>
                <button
                  type="button"
                  onClick={() => togglePhase(phase)}
                  aria-expanded={shown}
                  className={BUTTON_DISCLOSURE}
                >
                  <span>
                    {shown
                      ? `Hide emails for ${PHASE[phase].name}`
                      : `Show emails for ${PHASE[phase].name} (${emails.length})`}
                  </span>
                  <ChevronDown
                    size={18}
                    aria-hidden
                    className={`shrink-0 transition-transform ${shown ? "rotate-180" : ""}`}
                  />
                </button>
                {shown ? null : (
                  <p className="mt-1.5 text-[14px] text-[color:var(--color-ink-soft)]">
                    {PHASE[phase].hint}
                  </p>
                )}
              </>
            )}
            {shown ? (
              <>
                <h3
                  className={`flex items-center gap-2 text-[16px] font-bold text-[color:var(--color-ink)] ${
                    isCurrent ? "" : "mt-4"
                  }`}
                >
                  {PHASE[phase].title}
                  {isCurrent ? (
                    <span className="badge bg-[color:var(--color-amber)]/12 text-[color:var(--color-amber)]">
                      Now
                    </span>
                  ) : null}
                </h3>
                <ul className="mt-3 space-y-3">{emails.map(renderEmail)}</ul>
              </>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
